import { Link } from "react-router-dom";
import { Hammer, Clock, FileText, Boxes, CalendarDays, ShieldAlert, Briefcase, Users, Truck, CloudSun, Umbrella, Wind, ChevronRight, HardHat, Receipt, ArrowRight } from "lucide-react";
import { useSiteForecast, WX_TEXT } from "@/lib/useWeather";
import { initials, money, pct } from "./useDashboardV5";

const OFFICE = ["super_admin", "admin", "office"];
const OFFICE_QS = [...OFFICE, "quantity_surveyor"];
const OFFICE_FIELD = [...OFFICE, "field"];
// Same destinations and role rules as the existing "+ Create" menu (QuickAdd).
const QUICK = [
  ["New Job", "/app/jobs/new", Hammer, OFFICE],
  ["Add Timesheet", "/app/timesheets?new=1", Clock, OFFICE_FIELD],
  ["Create Quote", "/app/quotes/new", FileText, OFFICE_QS],
  ["Materials", "/app/materials", Boxes, OFFICE],
  ["Plan Crew", "/app/crew-planner?view=day", CalendarDays, OFFICE],
  ["Log Hazard", "/app/safety?report=hazard", ShieldAlert, OFFICE_FIELD],
];
const Skel = () => <span className="v5-skel" aria-hidden="true" />;
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

function Weather() {
  const { days, place } = useSiteForecast(1);
  const d = days?.[0];
  if (!d) return <div className="v5-weather" data-testid="v5-weather"><div className="v5-weatherTitle"><CloudSun size={26} /><span>Weather unavailable</span></div><div className="v5-weatherDetails"><span>Allow location or add your company address to see site weather.</span></div></div>;
  return (
    <div className="v5-weather" data-testid="v5-weather">
      <div className="v5-weatherTitle"><CloudSun size={26} /><span>{place || "Your area"}</span></div>
      <div className="v5-temp">{Math.round(d.temp_max)}°C <small>{WX_TEXT(d.weather_code)}</small></div>
      <div className="v5-weatherDetails"><span><Umbrella size={13} /> Rain {d.rain_prob ?? "—"}%</span><span><Wind size={13} /> Wind {d.wind_kmh != null ? Math.round(d.wind_kmh) : "—"} km/h</span></div>
    </div>
  );
}

export function Hero({ user }) {
  const date = new Intl.DateTimeFormat("en-NZ", { weekday: "long", day: "numeric", month: "short", year: "numeric", timeZone: "Pacific/Auckland" }).format(new Date());
  return (
    <section className="v5-hero" data-testid="v5-hero">
      <div className="v5-heroInner">
        <div>
          <div className="v5-date">{date}</div>
          <h1>Welcome back, <em>{(user.name || "").split(" ")[0]}</em></h1>
          <p>Here's what's happening with your business today.</p>
          <nav className="v5-qa" aria-label="Quick actions">
            {QUICK.filter((q) => q[3].includes(user.role)).map(([label, to, Icon]) => (
              <Link key={label} to={to} data-testid={`v5-qa-${slug(label)}`}><Icon aria-hidden="true" />{label}</Link>
            ))}
          </nav>
        </div>
        <Weather />
      </div>
    </section>
  );
}

function Kpi({ to, tone, icon: Icon, label, value, note, positive, footer, testid }) {
  return (
    <Link to={to} className="v5-kpi" data-testid={testid}>
      <span className={`v5-kicon is-${tone}`} aria-hidden="true"><Icon /></span>
      <div className="v5-kpiBody">
        <div className="v5-kpiLabel">{label}<ChevronRight aria-hidden="true" /></div>
        <div className="v5-kpiValueLine"><strong>{value}</strong>{note && <span className={`v5-kpiNote ${positive ? "positive" : ""}`}>{note}</span>}</div>
        <div className="v5-kpiFooter">{footer}</div>
      </div>
    </Link>
  );
}

export function Kpis({ d, loading }) {
  const s = d.stats || {};
  const jobs = d.jobs || [];
  const active = jobs.filter((j) => j.status === "in_progress");
  const valued = active.filter((j) => Number(j.quoted_value) > 0);
  const fleet = d.fleet || [];
  const maint = fleet.filter((f) => f.status === "maintenance").length;
  const team = (d.users || []).filter((u) => u.active !== false);
  const tsum = d.timesheets?.summary;
  const v = (x) => (loading ? <Skel /> : x ?? "—");
  return (
    <section className="v5-kpis" aria-label="Operational summary">
      <Kpi to="/app/jobs" tone="orange" icon={Briefcase} label="Jobs in progress" value={v(s.jobs_in_progress)} testid="v5-kpi-jobs"
        note={s.trend_7d ? `↗ ${s.trend_7d.jobs} new this week` : null} positive
        footer={<><div className="v5-meter"><i style={{ width: `${pct(s.jobs_in_progress, s.jobs_total)}%`, background: "#ff5f15" }} /></div>
          <span className="v5-kpiDetail">Quoted value<b>{valued.length ? money(valued.reduce((t, j) => t + Number(j.quoted_value), 0)) : "—"}</b></span></>} />
      <Kpi to="/app/team" tone="blue" icon={Users} label="Team members" value={v(s.team_total)} testid="v5-kpi-team"
        note={s.pending_leave != null ? `${s.pending_leave} leave pending` : null}
        footer={<><div className="v5-avatars" aria-label="Team">{team.slice(0, 3).map((u) => <span key={u.id} title={u.name}>{initials(u.name)}</span>)}{team.length > 3 && <span className="plus">+{team.length - 3}</span>}</div><span className="v5-kpiDetail">Active team</span></>} />
      <Kpi to="/app/plant/fleet" tone="green" icon={Truck} label="Active Fleet" value={v(s.fleet_active)} testid="v5-kpi-fleet"
        note={d.fleet ? `${maint} in maintenance` : null}
        footer={<><div className="v5-meter"><i style={{ width: `${pct(s.fleet_active, fleet.length)}%`, background: "#10a46c" }} /></div><span className="v5-kpiDetail">Fleet overview</span></>} />
      <Kpi to="/app/timesheets" tone="purple" icon={Clock} label="Timesheets awaiting approval" value={v(s.pending_timesheets)} testid="v5-kpi-timesheets"
        note={tsum ? `${tsum.total} logged today` : null}
        footer={<><div className="v5-meter"><i style={{ width: `${pct(tsum?.statuses?.approved || 0, tsum?.total)}%`, background: "#914df7" }} /></div><span className="v5-kpiDetail">Review timesheets</span></>} />
    </section>
  );
}

function VCard({ cls, icon: Icon, label, value, meter, sub, to, linkLabel, testid }) {
  return (
    <Link to={to} className={`v5-vcard ${cls}`} data-testid={testid}>
      <div className="v5-vtop"><span className="v5-vicon" aria-hidden="true"><Icon /></span><div><small>{label}</small><strong>{value}</strong>{sub && <p>{sub}</p>}</div></div>
      {meter != null && <div className="v5-vmeter"><i style={{ width: `${meter}%` }} /></div>}
      <span className="v5-vlink">{linkLabel} <ArrowRight size={13} /></span>
    </Link>
  );
}

export function BottomCards({ d, loading }) {
  const s = d.stats || {};
  const crew = new Set((d.assignments || []).filter((a) => a.job_id).map((a) => a.user_id)).size;
  const plant = new Set((d.dispatch?.vehicle_assignments || []).map((v) => v.vehicle_id)).size;
  const fleetTotal = (d.fleet || []).length;
  const tsum = d.timesheets?.summary;
  const od = d.panel?.overdue_invoices || [];
  const L = (x) => (loading ? "…" : x);
  return (
    <section className="v5-bottom" aria-label="Today">
      <VCard cls="crew" icon={HardHat} label="Crew dispatched today" value={L(d.assignments ? `${crew} / ${s.team_total ?? "—"}` : "Unavailable")} meter={pct(crew, s.team_total)} to="/app/crew-planner?view=day" linkLabel="View crew" testid="v5-card-crew" />
      <VCard cls="plant" icon={Truck} label="Plant allocated today" value={L(d.dispatch ? `${plant} / ${fleetTotal}` : "Unavailable")} meter={pct(plant, fleetTotal)} to="/app/plant/fleet" linkLabel="View plant" testid="v5-card-plant" />
      <VCard cls="time" icon={Clock} label="Today's timesheets" value={L(tsum ? `${Math.round(tsum.hours * 10) / 10} hrs` : "Unavailable")} sub={tsum ? `${tsum.total} logged` : null} to="/app/timesheets" linkLabel="View timesheets" testid="v5-card-timesheets" />
      <VCard cls="invoice" icon={Receipt} label="Recent invoices" value={L(d.panel ? <>{od.length} <em>overdue</em></> : "Unavailable")} sub={d.panel ? money(od.reduce((t, i) => t + Number(i.outstanding || 0), 0)) : null} to="/app/customer-invoices" linkLabel="View invoices" testid="v5-card-invoices" />
    </section>
  );
}
